import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';

import AppIcon from 'components/shared/AppIcon/AppIcon';
import { getPlugInIconPath } from 'utils/getResourcePath';
import { RoutePath } from '../../../../../constants/RoutePath';
import { useTypedDispatch } from '../../../../../hooks/useTypedDispatch';
import { setIsShowPlugInModal, setActivePlugIn } from '../../../../../store/plugin/pluginReducer';
import { deletePlugIn, removePlugInFromStore } from '../../../../../store/plugin/pluginThunk';
import PlugInModalDescription from '../../PluginModalDescription/PluginModalDescription';
import { IPlugInStore } from '../../../../../interfaces/IPlugIn';
import { callConfirmModal } from '../../../../../utils/callConfirmModal';

import './PlugInCard.scss';

export const MAXRATING = 5;

const PlugInCard: FC<IPlugInStore & { isGlobal?: boolean }> = ({
  _id, author, version, name, label, description,
  allowMultipleInstances, installed, creationDate, modificationDate,
  preferences, rating, numOfInstances, icon, isPrivate, allowedDbIds, isGlobal,
}) => {
  const { t, i18n } = useTranslation(['translation', 'plugIn', 'actions']);
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();

  const handleInstall = useCallback(() => {
    dispatch(setActivePlugIn({
      _id,
      author,
      version,
      name,
      label,
      description,
      allowMultipleInstances,
      installed,
      creationDate,
      modificationDate,
      preferences,
      rating,
      numOfInstances,
    }));
    navigate(`../..${RoutePath.PLUG_INS}/${name}/${version}`, { relative: 'path' });
  }, []);

  const handleUnInstall = useCallback(() => {
    callConfirmModal(
      {
        title: t('plugIn:confirmUninstall', { label }),
        onAgree() {
          dispatch(deletePlugIn(name, version));
        },
        okText: t('plugIn:buttons.unInstallPlugIn'),
        cancelText: t('translation:buttons.cancel'),
      },
      dispatch,
    );
  }, []);

  const handleModalOpen = useCallback(() => {
    dispatch(setIsShowPlugInModal({ isActive: true, _id }));
  }, [_id]);

  const handleRemoveFromStore = useCallback(() => {
    callConfirmModal(
      {
        title: t('plugIn:confirmRemoveFromStore'),
        cancelText: t('translation:buttons.cancel'),
        okText: t('plugIn:buttons.removePlugInInstance'),
        onAgree() { dispatch(removePlugInFromStore([_id])); },
      },
      dispatch,
    );
  }, [_id]);

  const installedMessage = useMemo(() => {
    if (allowMultipleInstances && installed) {
      return numOfInstances === 1 ? t('plugIn:installedOneTime', { numOfInstances }) : t('plugIn:installedTimes', { numOfInstances });
    }
    return (!allowMultipleInstances && installed) ? t('plugIn:alreadyInstalled') : '';
  }, [allowMultipleInstances, numOfInstances, installed, i18n.language]);

  const moreDetailsBtn = useMemo(() => (
    <Button
      variant="contained"
      type="submit"
      className="plug-in-card-more-details"
      onClick={handleModalOpen}
    >
      {t('plugIn:moreDetails')}
    </Button>
  ), [handleModalOpen, i18n.language]);

  const installBtn = useMemo(() => (
    <Button
      className="plugin-btn"
      variant="outlined"
      sx={{
        color: 'black',
        mr: 1,
        borderRadius: 50,
      }}
      disabled={installed && !allowMultipleInstances}
      onClick={handleInstall}
    >
      {t('plugIn:buttons.installPlugIn')}
    </Button>
  ), [handleInstall, i18n.language, installed, allowMultipleInstances]);

  const uninstallBtn = useMemo(() => (
    <Button
      className="plugin-btn"
      variant="outlined"
      sx={{
        color: 'black',
        mr: 1,
        borderRadius: 50,
      }}
      onClick={handleUnInstall}
    >
      {allowMultipleInstances ? t('plugIn:buttons.unInstallAllPlugIn') : t('plugIn:buttons.unInstallPlugIn')}
    </Button>
  ), [handleUnInstall, i18n.language, allowMultipleInstances]);

  const buttons = useMemo(() => {
    if (isGlobal) {
      return (
        <div className="plug-in-buttons">
          {moreDetailsBtn}
        </div>
      );
    }
    if (installed) {
      return (
        <div className="plug-in-buttons flex">
          <div>
            {installBtn}
            {uninstallBtn}
          </div>
          {moreDetailsBtn}
        </div>
      );
    }
    return (
      <div className="plug-in-buttons">
        {moreDetailsBtn}
        {installBtn}
      </div>
    );
  }, [installed, isGlobal, installBtn, uninstallBtn, moreDetailsBtn]);

  return (
    <Box key={_id} className={classNames('plug-in-card', { 'installed-plug-in-card': installed })}>
      <div className="plug-in-card-header">
        <Box>
          <div className="card-name">
            {label}
            {isGlobal && (
              <Tooltip title={t('plugIn:removePluginFromStore')} placement="top">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="remove-btn-icon"
                  onClick={handleRemoveFromStore}
                />
              </Tooltip>
            )}
          </div>
          {/* hide this part for the demo
          <div className="card-author">
            {t('plugIn:createdByAuthor', { author })}
          </div>
          */}
          <div className="plug-in-card-version">
            {`${t('plugIn:version')} ${version}`}
          </div>
        </Box>
        <AppIcon path={icon ? getPlugInIconPath(name, version, installed) : ''} alt={label} className="plug-in-card-logo" />
      </div>
      <div className="card-short-description">{description}</div>
      <div className="footer-wrapper-for-plug-in-card">
        {rating ? (
          <span className="plug-in-rating">
            <FontAwesomeIcon icon={faStar} />
            {`${rating}/${MAXRATING}`}
          </span>
        ) : (<span />)}
        <div>
          {buttons}
        </div>
      </div>
      <div className="plug-in-install-info">
        {installedMessage}
      </div>
      <PlugInModalDescription
        _id={_id}
        author={author}
        version={version}
        name={name}
        label={label}
        description={description}
        allowMultipleInstances={allowMultipleInstances}
        installed={installed}
        isGlobal={isGlobal}
        creationDate={creationDate}
        modificationDate={modificationDate}
        preferences={preferences}
        rating={rating}
        numOfInstances={numOfInstances}
        allowedDbIds={allowedDbIds}
        isPrivate={isPrivate}
        icon={icon}
      />
    </Box>
  );
};

export default PlugInCard;
